import { FC, useCallback, useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import { LoadingGroup, Spinner } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Title } from "capitalrx-components-2.0/lib/typography";

import { ContentPanel, CustomerSupport, PageWrapper } from "@/App/components";
import { ExperienceContext } from "@/App/context";
import { MemberEligibilityContext } from "@/App/context/MemberEligibilityContext";
import { useIsDiscountUser, useIsMedicareUser, useIsUnifiedUser } from "@/App/hooks";
import { MedicareBenefits } from "@/App/pages/Dashboard/Benefits";
import { AccumController } from "@/App/pages/Dashboard/Benefits/Accumulators/AccumController";
import { ANALYTIC_EVENTS, logEvent } from "@/App/service/analyticsService";
import { DisplayNetworkStatuses } from "@/App/types/accumulation";
import { ToggleButton, ToggleStatus } from "@/App/components/ToggleButton";

import { PharmacyPanel } from "./PharmacyPanel";
import { PlanDetailsPanel } from "./PlanDetailsPanel";
import { JudiCarePanel } from "./JudiCarePanel";
import { useListCategorizedExternalConnectionList } from "@/App/hooks/externalConnections";

export const DashboardPage: FC = () => {
  const [displayNetworkStatus, setDisplayNetworkStatus] = useState<DisplayNetworkStatuses>(DisplayNetworkStatuses.In);

  const { t: translate } = useTranslation();

  const { hideJudiCareOutboundSSO } = useContext(ExperienceContext);
  const { memberEligibility } = useContext(MemberEligibilityContext);
  const { hideMemberCard, hideCustomerCarePhoneNumber, hideHomeDeliveryCard, hideSpecialtyCard, hideCostcoHomeDeliveryCard, hideCostcoSpecialtyCard, hideAccumulators }
    = useContext(ExperienceContext);

  const { isDiscountUser } = useIsDiscountUser();
  const { isUnifiedUser } = useIsUnifiedUser();
  const { isMedicareUser } = useIsMedicareUser();

  const { externalConnectionsGeneral, externalConnectionsJudiCare, isLoading: isLoadingExternalConnections } = useListCategorizedExternalConnectionList({
    location: window.location.href,
  });

  const onChangeNetworkDisplayType = useCallback((value: ToggleStatus) => {
    if (value === "second") {
      setDisplayNetworkStatus(DisplayNetworkStatuses.Out);
      logEvent(ANALYTIC_EVENTS.MY_RX.outOfNetwork);
    } else {
      setDisplayNetworkStatus(DisplayNetworkStatuses.In);
      logEvent(ANALYTIC_EVENTS.MY_RX.inNetwork);
    }
  }, []);

  const showJudiCare = externalConnectionsJudiCare.length > 0 && !hideJudiCareOutboundSSO;

  const planContributions = (
    <Stack spacing="large">
      {!isDiscountUser && isUnifiedUser && !hideAccumulators && (
        <ToggleButton
          onChange={onChangeNetworkDisplayType}
          firstText={translate("DASHBOARD.dashboardPage.inNetwork")}
          secondText={translate("DASHBOARD.dashboardPage.outOfNetwork")}
        />
      )}

      {isMedicareUser && <MedicareBenefits />}
      {!isMedicareUser && !isDiscountUser && <AccumController displayNetworkStatus={displayNetworkStatus} />}
    </Stack>
  );

  return (
    <PageWrapper>
      <Stack>
        <ContentPanel
          showBorder={!isDiscountUser && !hideAccumulators}
          title={
            !isDiscountUser && (
              <Title testId="welcome-header">
                {translate("DASHBOARD.dashboardPage.welcomeHeader")}
                {memberEligibility.member.first_name || ""} {memberEligibility.member.last_name || ""}
              </Title>
            )
          }
        >
          {showJudiCare && (
            <JudiCarePanel />
          )}
          {!showJudiCare && planContributions}
        </ContentPanel>

        {showJudiCare && (
          <ContentPanel
            showBorder={true}
            title={
              <Title testId="plan-contributions-header">
                {translate("DASHBOARD.dashboardPage.planContributions")}
              </Title>
            }
          >
            {planContributions}
          </ContentPanel>
        )}

        {(!hideCustomerCarePhoneNumber || !hideMemberCard) && (
          <ContentPanel title={translate("DASHBOARD.dashboardPage.planDetails")} showBorder={true}>
            <PlanDetailsPanel />
          </ContentPanel>
        )}

        <LoadingGroup loading={isLoadingExternalConnections}>
          <LoadingGroup.Placeholder
            fallback={
              <Shelf>
                <Shelf.Spacer />
                <Spinner size="large" />
                <Shelf.Spacer />
              </Shelf>
            }
          >
            {(externalConnectionsGeneral?.length > 0 || !hideHomeDeliveryCard || !hideCostcoHomeDeliveryCard || !hideSpecialtyCard || !hideCostcoSpecialtyCard) && (
              <ContentPanel>
                <PharmacyPanel />
              </ContentPanel>
            )}
          </LoadingGroup.Placeholder>
        </LoadingGroup>

        {isUnifiedUser && (
          <ContentPanel showBorder={false}>
            <Shelf>
              <Shelf.Spacer />
              <CustomerSupport />
              <Shelf.Spacer />
            </Shelf>
          </ContentPanel>
        )}
      </Stack>
    </PageWrapper>
  );
};
